import { useMemo } from 'react';
import { Button, Grid, Box, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  ControlledField,
  DataTable,
  DatePickerInputField,
  SelectInputField,
  TimePickerInputField,
  VehicleClass,
  colors,
  getDateLongFormatWithLocale,
  removeEmptyStringKeys,
} from 'remote/global';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { VehicleSize } from 'utils/data/data-types';
import { PricingAdjustmentTypeEnum } from 'utils/data/enums';
import dayjs from 'dayjs';
import { DeleteOutline } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { isDateBeforeAnotherDate, getDaysOfWeekOptions } from 'utils/helpers';
import { TFunction } from 'i18next';
import {
  useDeleteOperatorsPricingSeasonalAdjustmentsMutation,
  useOperatorsPricingSeasonalAdjustmentsPostMutation,
  useOperatorsPricingSeasonalAdjustmentsPutMutation,
  useOperatorsVehicleClassesQuery,
  useOperatorsVehicleSizesQuery,
} from 'api/queries/operators';
import { PricingPeriodSchemaType, PricingPeriodsSchemaType, pricingPeriodsSchema } from './types';
import { EndAdornment, TooltipTableHeader } from '../form-helpers';
import { compareFieldArrays } from '../helpers';

// do not use undefined as default values as it breaks implementation
const vehicleClassDefaultOption = 'ALL';
const seatsRangeDefaultOption = 'ALL';
const emptyRow: PricingPeriodSchemaType = {
  id: '',
  type: PricingAdjustmentTypeEnum.DISCOUNT,
  validFrom: '',
  validTo: '',
  startTime: '',
  endTime: '',
  seatsRange: '',
  vehicleClass: '',
  daysOfWeek: [],
  adjustmentPct: 0,
};

const pricingPeriodTypeOptions = Object.values(PricingAdjustmentTypeEnum).map((d) => d);

const createVehicleClassOptions = (t: TFunction, operatorVehicleClasses: VehicleClass[]) => {
  const opts = [vehicleClassDefaultOption, ...operatorVehicleClasses.map((d) => d)];

  return opts.map((value) => ({
    label: t(`VehicleClass.${value}`),
    value: value !== vehicleClassDefaultOption ? value : '',
  }));
};

const createSeatsRangeOptions = (t: TFunction, operatorVehicleSizes: VehicleSize[]) => {
  const opts = [seatsRangeDefaultOption, ...operatorVehicleSizes.map((s) => s.seatsRange)];
  return opts.map((value) => ({
    label: t(`SeatsRange.${value}`),
    value: value !== seatsRangeDefaultOption ? value : '',
  }));
};

function CarrierPricingPeriodsForm({
  defaultValues,
  handlePageChange,
}: {
  defaultValues?: PricingPeriodsSchemaType;
  handlePageChange: (num: number) => void;
}) {
  const { data: vehicleSizes } = useOperatorsVehicleSizesQuery();
  const { data: vehicleClasses } = useOperatorsVehicleClassesQuery();
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<PricingPeriodsSchemaType>({
    defaultValues: {
      rows: [],
      ...defaultValues,
    },
    resolver: zodResolver(pricingPeriodsSchema),
  });
  const addPricingSeasonalAdjustmentsMutation =
    useOperatorsPricingSeasonalAdjustmentsPostMutation();
  const updatePricingSeasonalAdjustmentsMutation =
    useOperatorsPricingSeasonalAdjustmentsPutMutation();
  const removePricingSeasonalAdjustmentsMutation =
    useDeleteOperatorsPricingSeasonalAdjustmentsMutation();

  const { fields, prepend, remove } = useFieldArray({
    name: 'rows',
    control,
    keyName: 'fieldPropertyId',
  });

  const getIsRowExpired = (row: PricingPeriodSchemaType) => {
    if (row.validTo == null || row.validTo.length <= 0) return false;
    return isDateBeforeAnotherDate(row.validTo, dayjs().subtract(1, 'day').toDate());
  };

  const vehicleClassOptions = createVehicleClassOptions(t, vehicleClasses ?? []);
  const seatsRangeOptions = createSeatsRangeOptions(t, vehicleSizes ?? []);
  const pricingColumns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'validFrom',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.validFrom')}>
            {t('PricingView.period.table.header.validFrom')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.validFrom`}
              render={(renderProps) => (
                <DatePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    disabled: isRowExpired,
                    format: getDateLongFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'validTo',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.validTo')}>
            {t('PricingView.period.table.header.validTo')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.validTo`}
              render={(renderProps) => (
                <DatePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    disabled: isRowExpired,
                    format: getDateLongFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'daysOfWeek',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.daysOfWeek')}>
            {t('PricingView.period.table.header.daysOfWeek')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.daysOfWeek`}
              render={(renderProps) => (
                <SelectInputField
                  {...renderProps}
                  fieldProps={{
                    disabled: isRowExpired,
                    multiple: true,
                    renderValue: (value) => {
                      const opts = value?.map((v: string) =>
                        t(`DaysOfWeek.${v}`).slice(0, i18n.language === 'et' ? 1 : 2)
                      );
                      return opts?.length > 0 ? opts.join(', ') : t('DaysOfWeek.select');
                    },
                  }}
                  options={getDaysOfWeekOptions(tInstance)}
                />
              )}
            />
          );
        },
      },
      {
        field: 'startTime',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.startTime')}>
            {t('PricingView.period.table.header.startTime')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.startTime`}
              render={(renderProps) => (
                <TimePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disabled: isRowExpired,
                    value: dayjs(renderProps.field.value as string, 'HH:mm:ss'),
                    onChange: (value) =>
                      renderProps.field.onChange(
                        dayjs(value as unknown as string).format('HH:mm:ss')
                      ),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'endTime',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.endTime')}>
            {t('PricingView.period.table.header.endTime')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.endTime`}
              render={(renderProps) => (
                <TimePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disabled: isRowExpired,
                    value: dayjs(renderProps.field.value as string, 'HH:mm:ss') ?? null,
                    onChange: (value) =>
                      renderProps.field.onChange(
                        dayjs(value as unknown as string).format('HH:mm:ss')
                      ),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'seatsRange',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.vehicleSize')}>
            {t('PricingView.period.table.header.vehicleSize')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.seatsRange`}
              defaultValue=''
              render={(renderProps) => (
                <SelectInputField
                  {...renderProps}
                  options={seatsRangeOptions}
                  fieldProps={{
                    disabled: isRowExpired,
                    renderValue: (value) => {
                      if (value == null || value?.length === 0) return t(`SeatsRange.ALL`);
                      return t(`SeatsRange.${value}`);
                    },
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'vehicleClass',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.vehicleClass')}>
            {t('PricingView.period.table.header.vehicleClass')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.vehicleClass`}
              defaultValue='' // This is important as it will be set as default value if this property does not exist in previous values
              render={(renderProps) => {
                const isValid =
                  renderProps.field.value != null ||
                  (renderProps.field.value as unknown as string)?.length > 0;

                return (
                  <SelectInputField
                    {...renderProps}
                    options={vehicleClassOptions}
                    fieldProps={{
                      disabled: isRowExpired,
                      value: isValid ? (renderProps.field.value as any) : '', // Gets rid of mui error of undefined field
                      renderValue: (value) => {
                        if (value == null || value?.length === 0) return t(`VehicleClass.ALL`);
                        return t(`VehicleClass.${value}`);
                      },
                    }}
                  />
                );
              }}
            />
          );
        },
      },
      {
        field: 'adjustmentType',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.adjustmentType')}>
            {t('PricingView.period.table.header.adjustmentType')}
          </TooltipTableHeader>
        ),
        flex: 1,
        minWidth: 175,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.type`}
              render={(renderProps) => {
                return (
                  <SelectInputField
                    {...renderProps}
                    fieldProps={{
                      disabled: isRowExpired,
                      renderValue: (value) => {
                        const isValid = value != null && value.length > 0;
                        if (isValid) return t(`PricingPeriodType.${value}`);
                        return t('PricingPeriodType.select');
                      },
                    }}
                    options={pricingPeriodTypeOptions.map((value) => ({
                      label: t(`PricingPeriodType.${value}`),
                      value,
                    }))}
                  />
                );
              }}
            />
          );
        },
      },
      {
        field: 'adjustmentPct',
        renderHeader: () => (
          <TooltipTableHeader tooltip={t('PricingView.period.table.header.tooltip.adjustmentPct')}>
            {t('PricingView.period.table.header.adjustmentPct')}
          </TooltipTableHeader>
        ),
        minWidth: 200,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('rows').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const isRowExpired = getIsRowExpired(data.row);
          return (
            <ControlledField
              control={control}
              name={`rows.${index}.adjustmentPct`}
              fieldProps={{
                InputProps: {
                  disabled: isRowExpired,
                  endAdornment: <EndAdornment text='%' />,
                },
              }}
            />
          );
        },
      },
      {
        field: 'modify',
        headerName: ' ',
        minWidth: 50,
        sortable: false,
        renderCell: (data) => (
          <Box
            sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}
          >
            <IconButton
              size='large'
              sx={{ padding: 0, color: colors.gray2 }}
              onClick={async () => {
                const index = getValues('rows').findIndex((l) => l.id === data.id);
                if (index !== -1) remove(index);
              }}
            >
              <DeleteOutline color='primary' />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [
    t,
    i18n.language,
    control,
    getValues,
    remove,
    seatsRangeOptions,
    tInstance,
    vehicleClassOptions,
  ]);

  const onValid = async (data: PricingPeriodsSchemaType) => {
    // We have to call removeEmptyStringKeys because some form fields register as empty values on initial load such as vehicleClass and vehicleSize
    // Because BE doesn't include empty object keys, when a component tries to register object.[index].property and that index does not have that object property, it will be created by the first input component render
    const result = compareFieldArrays(defaultValues?.rows ?? [], removeEmptyStringKeys(data.rows));
    const { inserted, changed, removed } = result;

    try {
      await Promise.all([
        removed.map((item) =>
          removePricingSeasonalAdjustmentsMutation.mutate({ id: String(item.id) })
        ),
        inserted.map((item) => {
          // Delete the id and remove all empty string keys
          delete item.id;
          return addPricingSeasonalAdjustmentsMutation.mutate(removeEmptyStringKeys(item));
        }),
        changed.map((item) =>
          updatePricingSeasonalAdjustmentsMutation.mutate(removeEmptyStringKeys(item))
        ),
      ]);
    } catch (err) {
      // TODO: Sentry capture error
    }
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container spacing={3}>
        <Grid item xs={12} display='flex' justifyContent='space-between'>
          <Button
            onClick={() =>
              prepend({
                ...emptyRow,
                id: uuidv4(),
              })
            }
          >
            {t('PricingView.period.actions.addRow')}
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={isSubmitting || isValidating || isLoading}
          >
            {t('save')}
          </Button>
        </Grid>
        <Grid item xs={12} sx={{ width: '100%' }}>
          <DataTable
            rows={{ elements: fields }}
            columns={pricingColumns}
            loading={false}
            getRowId={(row) => row.id}
            onPageChange={(num) => handlePageChange(num)}
            t={t}
          />
        </Grid>
      </Grid>
    </form>
  );
}

export default CarrierPricingPeriodsForm;
